import { useState } from 'react';
import { Link } from 'react-router';
import { PAYMENT_PAGE, useOrders, usePayments } from '../api/orders.js';
import { Pager } from '../components/Pager.js';
import { QueryState } from '../components/QueryState.js';
import { formatDateTime, formatMoney } from '../format.js';
import { t } from '../i18n/uk.js';
import { ManualPaymentForm } from './OrderParts.js';

const tp = t.payments;

/** Finds an order by its number, then offers the manual payment form for it. */
function RecordByNumber() {
  const [number, setNumber] = useState('');
  const [search, setSearch] = useState<string | null>(null);
  const found = useOrders({ search: search ?? '' }, search !== null);
  const order = found.data?.items.find((o) => String(o.number) === search);
  return (
    <div className="card">
      <h2>{tp.recordTitle}</h2>
      <p className="muted hint">{tp.recordHint}</p>
      <div className="inline-form">
        <input
          aria-label={tp.orderNumber}
          placeholder={tp.orderNumber}
          inputMode="numeric"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setSearch(number.trim())}
        />
        <button type="button" onClick={() => setSearch(number.trim())}>
          {tp.findOrder}
        </button>
      </div>
      {search !== null && found.isSuccess && !order && (
        <p className="error-text">{tp.orderNotFound}</p>
      )}
      {order && (
        <>
          <p>
            <Link to={`/orders/${order.id}`}>
              {t.orders.title} № {order.number}
            </Link>{' '}
            · {order.customerName} · {formatMoney(order.total)} ·{' '}
            {t.orders.paymentStatuses[order.paymentStatus]}
          </p>
          <ManualPaymentForm
            key={order.id}
            orderId={order.id}
            defaultMethod={order.paymentMethod ?? 'bank_transfer'}
          />
        </>
      )}
    </div>
  );
}

/** Payments (PAY-11a): everything received and refunded, newest first; manual entries. */
export function PaymentsPage() {
  const [direction, setDirection] = useState('');
  const [offset, setOffset] = useState(0);
  const payments = usePayments({ direction: direction || undefined, offset });
  return (
    <section className="wide">
      <h1>{tp.title}</h1>
      <RecordByNumber />
      <div className="inline-form">
        <label>
          {tp.direction}
          <select
            aria-label={tp.direction}
            value={direction}
            onChange={(e) => {
              setDirection(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">{t.common.all}</option>
            <option value="in">{tp.directions.in}</option>
            <option value="refund">{tp.directions.refund}</option>
          </select>
        </label>
      </div>
      <QueryState query={payments}>
        {(page) =>
          page.items.length === 0 ? (
            <p className="muted">{tp.empty}</p>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th>{tp.receivedAt}</th>
                    <th>{tp.order}</th>
                    <th>{tp.direction}</th>
                    <th className="num">{t.orders.total}</th>
                    <th>{tp.method}</th>
                    <th>{tp.provider}</th>
                    <th>{tp.reason}</th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map((p) => (
                    <tr key={p.id}>
                      <td>{formatDateTime(p.receivedAt)}</td>
                      <td>
                        <Link to={`/orders/${p.orderId}`}>{tp.openOrder}</Link>
                      </td>
                      <td>{tp.directions[p.direction]}</td>
                      <td className="num">
                        {p.direction === 'refund' ? '−' : ''}
                        {formatMoney(p.amount)}
                      </td>
                      <td>{t.orders.methods[p.method]}</td>
                      <td>{tp.providers[p.provider] ?? p.provider}</td>
                      <td className="muted">
                        {[p.reference, p.reason].filter(Boolean).join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pager
                offset={offset}
                pageSize={PAYMENT_PAGE}
                total={page.total}
                onChange={setOffset}
              />
            </>
          )
        }
      </QueryState>
    </section>
  );
}
